import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Globe, 
  MessageCircle, 
  Luggage, 
  Languages, 
  Smartphone,
  Shield,
  Sparkles,
  ChevronRight,
  MapPin,
  Heart,
  Utensils,
  Phone,
  Check,
  Users
} from "lucide-react";
import ComingSoonModal from "../components/ComingSoonModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

// Helper to check if JWT token is valid (not expired)
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return Date.now() < exp;
  } catch {
    return false;
  }
};

// Language options for pre-auth selection with SVG flag URLs
const languageOptions = [
  { code: "en", flagUrl: "https://flagcdn.com/w40/us.png", flagUrl2x: "https://flagcdn.com/w80/us.png", label: "English", nativeLabel: "English" },
  { code: "es", flagUrl: "https://flagcdn.com/w40/es.png", flagUrl2x: "https://flagcdn.com/w80/es.png", label: "Spanish", nativeLabel: "Español" },
  { code: "fr", flagUrl: "https://flagcdn.com/w40/fr.png", flagUrl2x: "https://flagcdn.com/w80/fr.png", label: "French", nativeLabel: "Français" },
  { code: "ar", flagUrl: "https://flagcdn.com/w40/sa.png", flagUrl2x: "https://flagcdn.com/w80/sa.png", label: "Arabic", nativeLabel: "العربية", rtl: true }
];

// Asia region option (links to sub-selection page)
const asiaOption = {
  type: "region",
  code: "asia",
  icon: "🌏",
  nativeLabel: "Asia",
  subtitle: "Chinese • Japanese • Korean • Vietnamese • Thai"
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Coming Soon Modal state
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  
  // Check if language has been selected (for pre-auth language-first flow)
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => {
    return !!localStorage.getItem('passportTalk_primaryLanguage');
  });
  const [selectedLang, setSelectedLang] = useState(null);

  // Auth-aware redirect: if user has valid session, redirect to appropriate page
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (isTokenValid(token)) {
      const primaryLanguage = localStorage.getItem('passportTalk_primaryLanguage');
      
      if (!primaryLanguage) {
        navigate('/language-select', { replace: true });
      } else if (primaryLanguage === 'en') {
        navigate('/home', { replace: true });
      } else {
        navigate('/conversation', { replace: true });
      }
    }
  }, [navigate]);

  // Handle language selection (pre-auth)
  const handleLanguageSelect = (lang) => {
    setSelectedLang(lang.code);
    
    // Save to localStorage
    localStorage.setItem('passportTalk_primaryLanguage', lang.code);
    
    // Update i18n immediately so UI updates
    i18n.changeLanguage(lang.code);
    
    // Small delay for visual feedback, then show landing page
    setTimeout(() => {
      setHasSelectedLanguage(true);
    }, 300);
  };

  // If no language selected yet, show language-first selection
  if (!hasSelectedLanguage) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          {/* Logo */}
          <img 
            src={LOGO_URL} 
            alt="PassportTalk" 
            className="w-20 h-20 mx-auto mb-6 object-contain"
          />
          
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-outfit mb-2">
            Welcome to PassportTalk
          </h1>
          <p className="text-slate-600 mb-8">
            Choose your language to get started
          </p>

          {/* Language Options */}
          <div className="space-y-3">
            {languageOptions.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang)}
                className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ease-out group
                  ${selectedLang === lang.code 
                    ? 'border-[#0EA5E9] bg-[#0EA5E9]/5 shadow-lg scale-[1.02]' 
                    : 'border-slate-200 bg-white hover:border-[#0EA5E9]/50 hover:shadow-md hover:-translate-y-0.5'
                  }`}
                dir={lang.rtl ? 'rtl' : 'ltr'}
                data-testid={`landing-lang-${lang.code}`}
              >
                <div className={`flex items-center gap-4 ${lang.rtl ? 'flex-row-reverse' : ''}`}>
                  {/* Flag Icon */}
                  <div className="w-10 h-7 rounded overflow-hidden shadow-sm border border-slate-200 flex-shrink-0 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                    <img 
                      src={lang.flagUrl}
                      srcSet={`${lang.flagUrl2x} 2x`}
                      alt={`${lang.label} flag`}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                  <span className={`flex-1 font-semibold text-slate-800 ${lang.rtl ? 'text-right' : 'text-left'}`}>
                    {lang.nativeLabel}
                  </span>
                  {selectedLang === lang.code && (
                    <Check className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}

            {/* Asia Region Card */}
            <button
              onClick={() => {
                setSelectedLang('asia');
                setTimeout(() => navigate('/asia-languages'), 200);
              }}
              className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ease-out group
                ${selectedLang === 'asia' 
                  ? 'border-red-500 bg-red-500/5 shadow-lg scale-[1.02]' 
                  : 'border-slate-200 bg-white hover:border-red-500/50 hover:shadow-md hover:-translate-y-0.5'
                }`}
              data-testid="landing-lang-asia"
            >
              <div className="flex items-center gap-4">
                {/* Region Icon */}
                <div className="w-10 h-7 rounded overflow-hidden shadow-sm border border-slate-200 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                  <span className="text-xl">{asiaOption.icon}</span>
                </div>
                <div className="flex-1 text-left">
                  <span className="font-semibold text-slate-800">{asiaOption.nativeLabel}</span>
                  <p className="text-xs text-slate-500 mt-0.5">{asiaOption.subtitle}</p>
                </div>
                {selectedLang === 'asia' && (
                  <Check className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
              </div>
            </button>
          </div>

          {/* Early Access badge */}
          <div className="mt-8">
            <span className="px-3 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full">
              Early Access
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Main landing page (shown after language selection)
  return (
    <div className={`min-h-screen bg-white ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="PassportTalk" className="w-8 h-8 object-contain" />
            <span className="font-bold text-lg text-slate-800 font-outfit">{t('app.name')}</span>
            <span className="px-2 py-0.5 bg-[#0EA5E9]/10 text-[#0EA5E9] text-[10px] font-medium rounded-full">Early Access</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <button
              onClick={() => setHasSelectedLanguage(false)}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
              title="Change language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <Link 
              to="/login"
              className="px-4 py-2 bg-gradient-to-r from-[#0EA5E9] to-[#06b6d4] text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all"
              data-testid="nav-try-beta"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#0EA5E9]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#10b981]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center mb-6">
            <img src={LOGO_URL} alt="PassportTalk" className="w-20 h-20 sm:w-24 sm:h-24 object-contain" />
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-outfit mb-6 leading-tight">
            {t('landing.heroTitle')}<br />
            <span className="bg-gradient-to-r from-[#0EA5E9] to-[#10b981] bg-clip-text text-transparent">
              {t('landing.heroHighlight')}
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t('landing.heroSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#06b6d4] text-white font-semibold rounded-full hover:shadow-xl hover:scale-105 transition-all text-lg"
              data-testid="hero-cta"
            >
              {t('landing.ctaTryBeta')}
              <ChevronRight className="w-5 h-5" />
            </Link>
            <a 
              href="#features"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-slate-200 text-slate-700 font-semibold rounded-full hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-all text-lg"
            >
              {t('landing.ctaHowItWorks')}
            </a>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#10b981]" />
              <span>{t('landing.trustNoCard')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0EA5E9]" />
              <span>{t('landing.trustAI')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#f59e0b]" />
              <span>{t('landing.trustDialects')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {/* Dialect Aware */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#f59e0b] to-[#f97316] flex items-center justify-center mb-6">
                <Languages className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 font-outfit mb-3">
                {t('landing.featureDialectTitle')}
              </h3>
              <p className="text-slate-600 mb-4">
                {t('landing.featureDialectDesc')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">🇩🇴 Dominican</span>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">🇲🇽 Mexican</span>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">🇪🇸 Spanish</span>
              </div>
            </div>
            
            {/* WhatsApp Ready */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#10b981] to-[#059669] flex items-center justify-center mb-6">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 font-outfit mb-3">
                {t('landing.featureWhatsAppTitle')}
              </h3>
              <p className="text-slate-600 mb-4">
                {t('landing.featureWhatsAppDesc')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">{t('common.copy')}</span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">{t('common.shareWhatsApp')}</span>
              </div>
            </div>
            
            {/* Gender Aware */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 font-outfit mb-3">
                {t('landing.featureGenderTitle')}
              </h3>
              <p className="text-slate-600 mb-4">
                {t('landing.featureGenderDesc')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-violet-50 text-violet-700 text-xs font-medium rounded-full">{t('conversation.genderWoman')}</span>
                <span className="px-3 py-1 bg-violet-50 text-violet-700 text-xs font-medium rounded-full">{t('conversation.genderMan')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit mb-4">
              {t('landing.modesTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.modesSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Travel Mode */}
            <div className="relative bg-gradient-to-br from-[#0EA5E9]/5 to-[#06b6d4]/10 rounded-3xl p-6 sm:p-8 border-2 border-[#0EA5E9]/20 hover:border-[#0EA5E9]/40 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#06b6d4] flex items-center justify-center shadow-lg flex-shrink-0">
                  <Luggage className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 font-outfit mb-1">
                    🧳 {t('landing.travelModeTitle')}
                  </h3>
                  <p className="text-slate-600">{t('home.travelCardDesc')}</p>
                </div>
              </div>
              
              <p className="text-slate-700 mb-6">
                {t('landing.travelModeDesc')}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Phone className="w-4 h-4 text-[#0EA5E9]" />
                  </div>
                  <span>{t('travel.scenarioEmergency')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Utensils className="w-4 h-4 text-[#0EA5E9]" />
                  </div>
                  <span>{t('travel.scenarioFood')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <MapPin className="w-4 h-4 text-[#0EA5E9]" />
                  </div>
                  <span>{t('travel.scenarioDirections')}</span>
                </div>
              </div>
            </div>
            
            {/* Conversation Mode */}
            <div className="relative bg-gradient-to-br from-[#10b981]/5 to-[#059669]/10 rounded-3xl p-6 sm:p-8 border-2 border-[#10b981]/20 hover:border-[#10b981]/40 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#10b981] to-[#059669] flex items-center justify-center shadow-lg flex-shrink-0">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 font-outfit mb-1">
                    💬 {t('landing.everydayModeTitle')}
                  </h3>
                  <p className="text-slate-600">{t('home.everydayCardDesc')}</p>
                </div>
              </div>
              
              <p className="text-slate-700 mb-6">
                {t('landing.everydayModeDesc')}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Sparkles className="w-4 h-4 text-[#10b981]" />
                  </div>
                  <span>{t('conversation.instantToggle')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Globe className="w-4 h-4 text-[#10b981]" />
                  </div>
                  <span>{t('conversation.dialect')}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Smartphone className="w-4 h-4 text-[#10b981]" />
                  </div>
                  <span>{t('common.shareWhatsApp')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit mb-4">
              {t('landing.useCasesTitle')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: "✈️", titleKey: "landing.useCaseTravelers", descKey: "landing.useCaseTravelersDesc" },
              { icon: "💕", titleKey: "landing.useCaseCouples", descKey: "landing.useCaseCouplesDesc" },
              { icon: "🏢", titleKey: "landing.useCaseProfessionals", descKey: "landing.useCaseProfessionalsDesc" },
              { icon: "📱", titleKey: "landing.useCaseSocial", descKey: "landing.useCaseSocialDesc" },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 text-center hover:shadow-md transition-shadow border border-slate-100">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h4 className="font-bold text-slate-900 mb-1">{t(item.titleKey)}</h4>
                <p className="text-sm text-slate-600">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Agents Coming Soon Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4" />
            <span>Coming June 2026</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit mb-4">
            Live Agent Support
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Sometimes AI isn't enough. Connect with real bilingual experts for help with housing, banking, government paperwork, and more.
          </p>
          
          <button
            onClick={() => setShowComingSoonModal(true)}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-full hover:shadow-xl hover:scale-105 transition-all text-lg"
            data-testid="landing-live-agents-cta"
          >
            Be First to Access
            <ChevronRight className="w-5 h-5" />
          </button>
          <p className="text-sm text-slate-500 mt-4">VIP users get priority access</p>
        </div>
      </section>

      {/* Main CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-r from-[#0EA5E9] to-[#10b981] rounded-3xl p-8 sm:p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit mb-4">
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto">
              {t('landing.ctaSubtitle')}
            </p>
            <Link 
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#0EA5E9] font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all text-lg"
              data-testid="cta-get-started"
            >
              {t('landing.ctaButton')}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Early Access Notice */}
      <section className="py-8 bg-sky-50 border-y border-sky-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sky-800 text-sm">
            <strong>{t('app.earlyAccess')}:</strong> {t('landing.earlyAccessNotice')}{" "}
            <Link to="/beta" className="underline hover:text-sky-900">{t('profile.betaNotice')}</Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="PassportTalk" className="w-8 h-8 object-contain" />
              <span className="font-bold text-lg font-outfit">{t('app.name')}</span>
              <span className="px-2 py-0.5 bg-white/10 text-white/80 text-xs font-semibold rounded-full">{t('app.earlyAccess')}</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <Link to="/terms" className="hover:text-white transition-colors">{t('profile.terms')}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">{t('profile.privacy')}</Link>
              <Link to="/refunds" className="hover:text-white transition-colors">{t('profile.refunds')}</Link>
              <Link to="/beta" className="hover:text-white transition-colors">{t('profile.betaNotice')}</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} PassportTalk. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName="Live Agents"
        launchDate="June 2026"
        source="landing_page"
      />
    </div>
  );
};

export default LandingPage;
